import {defineStore} from 'pinia'
import {computed, readonly, ref} from 'vue'
import type {useContext, useDialogs, useProcesses} from '@hantera/portal-app'
import type useBackend from '@/composition/useBackend'
import type {Role} from '@/backend/iam.types'
import {useIamEventsStore} from './iam-events-store'

export const useRoleStore = (
  roleId: string,
  services: {
    context: ReturnType<typeof useContext>
    dialogs: ReturnType<typeof useDialogs>
    processes: ReturnType<typeof useProcesses>
    iamService: ReturnType<typeof useBackend>['iam']
    t: (key: string, params?: Record<string, any>) => string
  },
) => {
  return defineStore(`role-${roleId}`, () => {
    // === STATE ===
    const role = ref<Role | null>(null)
    const isLoading = ref(false)

    // Pending changes for edit mode
    const pendingKey = ref<string | null>(null)
    const pendingDescription = ref<string | null>(null)
    const pendingPermissions = ref<string[] | null>(null)

    // === SERVICES (from parameters) ===
    const { context, dialogs, processes, iamService, t } = services

    // === PERMISSIONS ===
    const canWrite = computed(() => context.authorize('iam/roles:write'))

    // === COMPUTED ===
    const isSystemRole = computed(() => role.value?.key.startsWith('system:') || false)

    const canEdit = computed(() => canWrite.value && !isSystemRole.value)

    const canDeleteRole = computed(() => canWrite.value && !isSystemRole.value && (role.value?.memberCount.toNumber() ?? 0) === 0)

    const hasUnsavedDescription = computed(() => pendingDescription.value !== null)

    const hasUnsavedPermissions = computed(() => pendingPermissions.value !== null)

    // Display values (with pending changes)
    const displayKey = computed({
      get: () => pendingKey.value ?? role.value?.key ?? '',
      set: (value) => {
        pendingKey.value = value
      },
    })

    const displayDescription = computed({
      get: () => pendingDescription.value ?? role.value?.description ?? '',
      set: (value) => {
        pendingDescription.value = value
      },
    })

    const displayPermissions = computed({
      get: () => pendingPermissions.value ?? role.value?.acl ?? [],
      set: (value) => {
        pendingPermissions.value = value
      },
    })

    // === ACTIONS ===
    async function refresh() {
      if (!roleId) return

      isLoading.value = true
      try {
        role.value = await iamService.getRole(roleId)
      } catch (error: any) {
        console.error('Failed to load role:', error)
        processes.error({ message: t('failedToLoad') })
      } finally {
        isLoading.value = false
      }
    }

    async function saveDescription() {
      if (!role.value || pendingDescription.value === null) return

      try {
        await iamService.updateRole(
          role.value.key,
          { description: pendingDescription.value },
          role.value.eTag
        )

        pendingDescription.value = null
        await refresh()
        processes.createProcess({ description: t('descriptionUpdated') }).complete()
      } catch (error: any) {
        console.error('Failed to save description:', error)
        processes.error({ message: t('failedToSave') })
      }
    }

    async function savePermissions() {
      if (!role.value || pendingPermissions.value === null) return

      try {
        await iamService.updateRoleAcl(
          role.value.key,
          pendingPermissions.value,
          role.value.eTag
        )

        pendingPermissions.value = null
        await refresh()
        processes.createProcess({ description: t('permissionsUpdated') }).complete()
      } catch (error: any) {
        console.error('Failed to save permissions:', error)
        processes.error({ message: t('failedToSave') })
      }
    }

    function discardDescription() {
      pendingDescription.value = null
    }

    function discardPermissions() {
      pendingPermissions.value = null
    }

    async function deleteRole() {
      if (!role.value) return

      if ((role.value.memberCount?.toNumber() ?? 0) > 0) {
        processes.error({
          message: t('cannotDeleteWithMembers', { count: role.value.memberCount }),
        })
        return
      }

      if (isSystemRole.value) {
        processes.error({ message: t('cannotDeleteSystemRole') })
        return
      }

      const confirmed = await dialogs.confirm(
        t('confirmDelete', { key: role.value.key }),
        t('deleteRole'),
        t('delete'),
        t('cancel'),
        true, // destructive
      )

      if (!confirmed) return

      try {
        await iamService.deleteRole(role.value.key)
        
        // Broadcast to all RolesView instances
        const iamEvents = useIamEventsStore()
        iamEvents.emitRolesListChanged()
        
        processes.createProcess({ description: t('roleDeleted') }).complete()
      } catch (error: any) {
        console.error('Failed to delete role:', error)
        processes.error({ message: t('failedToDelete') })
      }
    }

    // Member removal is now handled directly in RoleMembersTab
    // These functions remain for backwards compatibility but just refresh the role

    return {
      // State
      role,
      isLoading: readonly(isLoading),

      // Pending changes
      pendingKey,
      pendingDescription,
      pendingPermissions,

      // Display values (with pending)
      displayKey,
      displayDescription,
      displayPermissions,

      // Computed
      isSystemRole,
      canEdit,
      canDeleteRole,
      hasUnsavedDescription,
      hasUnsavedPermissions,

      // Services
      dialogs,
      processes,
      iamService,

      // Actions
      refresh,
      saveDescription,
      savePermissions,
      discardDescription,
      discardPermissions,
      deleteRole,
    }
  })()
}
